// DataSetupPage — how each form template lands in SQL. A thin shell: the header,
// the tab strip, and the two view preferences that must survive a tab switch
// (density, and the badges' last-known totals). Everything else belongs to the
// tabs, each of which owns its own endpoint, mutations, drafts and toolbar:
//
//   · FormMetaTab      — "Form meta & SQL", POST /dashboard-setup/list-forms
//   · FormStructureTab — "Form structure",  POST /dashboard-setup/fetch-structure
//
// A tab is MOUNTED only while active (the ternary below), so each tab's queries
// need no `skip`, and its unsaved drafts reset on switch — the same lifetime the
// Data structure page's tabs have. A guard against leaving with unsaved picks is
// its own future chunk.
//
// Tab badges — each entity's last known UNFILTERED total (the DataStructurePage
// idiom), undefined until its tab has been opened once, so neither costs a
// standing count query. Each tab reports through `onCount`; the meta tab guards
// on its own search box, the structure list takes no search at all.
//
// PERMISSIONS — module-level access gates on the `dashboard_setup` module (the
// nav item's moduleSlug in mocks/nav.ts hides nav entry and route together, the
// same mechanism as every other module). The confirmed GET /modules payload
// carries `view` / `import` / `export` / `sql_export` ("SQL export") /
// `manage_sets` ("Manage Sets") — NOT the legacy nested flags
// (companySQL/formSQL/formStructureSQL), so never wire those names. The finer
// IN-PAGE gates are still unwired — which extra guards the SQL settings
// surface, the per-row data-connection toggle, and the Form structure tab's
// export is its own future chunk: add one named hook per extra to
// hooks/useModuleAccess.ts and apply them at those sites. Copy the slugs
// verbatim from the payload — a mistyped extra fails silently.

import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tabs, type TabItem } from '@/components/common';
import FormMetaTab from './FormMetaTab';
import FormStructureTab from './FormStructureTab';
import { type Density } from './DensityToggle';

type DataSetupTab = 'form-meta' | 'form-structure';

const DataSetupPage = () => {
  const { t } = useTranslation();

  const [tab, setTab] = useState<DataSetupTab>('form-meta');
  const [density, setDensity] = useState<Density>('default');

  const [lastTotals, setLastTotals] = useState<{
    formMeta?: number;
    formStructure?: number;
  }>({});
  // Stable, and no-ops when unchanged, so neither child can drive a loop.
  const recordMetaCount = useCallback(
    (n: number) => setLastTotals((c) => (c.formMeta === n ? c : { ...c, formMeta: n })),
    [],
  );
  const recordStructureCount = useCallback(
    (n: number) => setLastTotals((c) => (c.formStructure === n ? c : { ...c, formStructure: n })),
    [],
  );

  const tabs: TabItem[] = [
    {
      value: 'form-meta',
      label: t('data_setup_tab_meta'),
      icon: 'database',
      count: lastTotals.formMeta,
    },
    {
      value: 'form-structure',
      label: t('data_setup_tab_structure'),
      icon: 'table-2',
      count: lastTotals.formStructure,
    },
  ];

  return (
    <div className='data-setup-page'>
      <header className='data-setup-page__header'>
        <div>
          <h1 className='data-setup-page__title'>{t('data_setup_title')}</h1>
          <p className='data-setup-page__subtitle'>{t('data_setup_subtitle')}</p>
        </div>
      </header>

      <Tabs
        value={tab}
        onChange={(v) => setTab(v as DataSetupTab)}
        items={tabs}
        aria-label={t('data_setup_title')}
      />

      {tab === 'form-meta' ? (
        <FormMetaTab density={density} onDensity={setDensity} onCount={recordMetaCount} />
      ) : (
        <FormStructureTab density={density} onDensity={setDensity} onCount={recordStructureCount} />
      )}
    </div>
  );
};

export default DataSetupPage;
